import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogActions,
  DialogTitle,
  FormGroup,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  Chip,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Paper,
  Container,
  Fade,
  Zoom,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import { capitalize, debounce } from "lodash";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import PersonIcon from "@mui/icons-material/Person";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { apiAuth, LeagueManagementService, userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import { STATUS_OPTIONS_LIST, STATUS_OPTIONS_LIST1 } from "../../utils/constants";
import ViewUserManagement from "./ViewUser";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EditOutlined } from "@mui/icons-material";
import { Loader } from "../../components/CustomLoader";
import { fDate } from "../../utils/formatTime";
import { Helmet } from "react-helmet-async";
import { calculateAge, TrimText } from "../../utils/helpers";
import { 
  initializeFromUrlParams, 
  updateSearchParams, 
  updatePageParams, 
  updateFilterParams, 
  updateViewModeParams 
} from "../../utils/urlParams";
import "./UserManagement.css";

export default function UserManagementList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState([]);
  const [show, setShow] = useState(false);
  const [warning, setWarning] = useState("");
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [viewId, setViewId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [country, setCountry] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [totalActiveUsers, setTotalActiveUsers] = useState(0);
  const [totalInactiveUsers, setTotalInactiveUsers] = useState(0);
  const [totalSelfExcludedUsers, setTotalSelfExcludedUsers] = useState(0);
  const [allUsers, setAllUsers] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchAllUsersForStats = async () => {
    try {
      const body = {
        searchItem: "",
        pageNumber: 1,
        pageSize: 1000, // Get all users for stats
        status: [],
        country: [],
      };
      const res = await userServices.getUser(body);
      if (res.status === 200 && res?.data?.data?.result) {
        const allUsersData = res.data.data.result;
        setAllUsers(allUsersData);

        // Calculate total stats from all users
        setTotalActiveUsers(allUsersData.filter(item => item.status === 1).length);
        setTotalInactiveUsers(allUsersData.filter(item => item.status === 0).length);
        setTotalSelfExcludedUsers(allUsersData.filter(item => item.status === 3).length);
      }
    } catch (err) {
      console.error('Error fetching all users for stats:', err);
    }
  };

  const fetchUsers = async (query) => {
    const body = {
      searchItem: query || "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
      country: selectedCountry,
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await userServices.getUser(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.result);
          setTotalPages(res?.data?.data?.totalRecords);
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      // Update URL parameter after debounce
      updateSearchParams(searchParams, setSearchParams, query, 1);
      fetchUsers(query);
    }, 400),
    [currentPage, pageSize, status, searchParams, setSearchParams]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    setCurrentPage(1);
    
    // Don't update URL immediately - let debouncedSearch handle it
    debouncedSearch(query);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Update URL parameter to maintain page state
    updatePageParams(searchParams, setSearchParams, page);
  };

  const handleCountryChange = (newCountry) => {
    setSelectedCountry(newCountry);
    setCurrentPage(1);
    
    // Update URL parameter
    updateFilterParams(searchParams, setSearchParams, 'country', newCountry, 1);
  };

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    setCurrentPage(1);
    
    // Update URL parameter
    updateFilterParams(searchParams, setSearchParams, 'status', newStatus, 1);
  };

  const deleteData = async (id) => {
    Swal.fire({
      text: "Are you sure you want to delete this user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await userServices.deleteUser({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            setData((prevData) => prevData.filter((item) => item._id !== id));
            setTotalPages((prevTotal) => prevTotal - 1);
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const res = await userServices.changeStatus({
        id,
        status: newStatus,
      });

      if (res.status === 200) {
        toast.success(res?.data?.message, { toastId: "success" });
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  const fetchCountry = async () => {
    try {
      const response = await apiAuth.getAllCountry();
      if (response.status == 200) {
        setCountry(response?.data?.data?.result || [])
      }

    } catch (error) {
      toast.error(error?.response?.data?.message || "Error in fetching Country.")
    }
  }
  // Initialize state from URL parameters
  useEffect(() => {
    const pageFromUrl = searchParams.get('page');
    const viewModeFromUrl = searchParams.get('view');
    const searchFromUrl = searchParams.get('search');
    const countryFromUrl = searchParams.get('country');
    const statusFromUrl = searchParams.get('status');
    const pageSizeFromUrl = searchParams.get('pageSize');
    
    console.log('UserManagement - Initializing from URL params:', {
      pageFromUrl,
      viewModeFromUrl,
      searchFromUrl,
      countryFromUrl,
      statusFromUrl,
      pageSizeFromUrl
    });
    
    // Update state
    if (pageFromUrl && !isNaN(pageFromUrl) && parseInt(pageFromUrl) > 0) {
      setCurrentPage(parseInt(pageFromUrl));
    }
    
    if (viewModeFromUrl && (viewModeFromUrl === 'grid' || viewModeFromUrl === 'list')) {
      setViewMode(viewModeFromUrl);
    }
    
    if (searchFromUrl) {
      setSearchItem(searchFromUrl);
    }
    
    if (countryFromUrl) {
      const countryArray = countryFromUrl.split(',').filter(Boolean);
      setSelectedCountry(countryArray);
    }
    
    if (statusFromUrl) {
      const statusArray = statusFromUrl.split(',').map(s => parseInt(s)).filter(s => !isNaN(s));
      setStatus(statusArray);
    }
    
    if (pageSizeFromUrl && !isNaN(pageSizeFromUrl) && parseInt(pageSizeFromUrl) > 0) {
      setPageSize(parseInt(pageSizeFromUrl));
    }
    
    // Use setTimeout to ensure all state updates are processed
    setTimeout(() => {
      setIsInitialized(true);
    }, 50);
  }, [searchParams]);

  useEffect(() => {
    fetchCountry();
    fetchAllUsersForStats(); // Fetch all users for stats on component mount
  }, [])


  useEffect(() => {
    // Only fetch data after initialization is complete
    if (!isInitialized) return;
    
    console.log('UserManagement - Fetching data with params:', {
      searchItem,
      currentPage,
      pageSize,
      status,
      selectedCountry
    });
    
    fetchUsers(searchItem);
  }, [isInitialized, currentPage, pageSize, status, refresh, selectedCountry]);

  const renderUserCard = (user, index) => (
    <Zoom in={!isLoading} className={`user-card-zoom user-card-zoom-${index}`}>
      <Grid item xs={12} sm={6} md={4} lg={3} key={user._id}>
        <Card className="user-card">
          <Box className="user-card-index-badge">
            {(currentPage - 1) * pageSize + index + 1}
          </Box>

          <CardContent className="user-card-content">
            <Box className="user-card-header">
              <Avatar className="user-card-avatar">
                <PersonIcon />
              </Avatar>
              <Box className="user-card-title-container">
                {/* Full Name Display */}
                <Typography variant="h6" className="user-card-fullname" sx={{
                  color: 'white',
                  fontWeight: 600,
                  mb: 0.5,
                  fontSize: '1.1rem',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                }}>
                  {(user.firstname && user.lastname)
                    ? `${user.firstname} ${user.lastname}`
                    : (user.firstname || user.lastname)
                      ? `${user.firstname || ''} ${user.lastname || ''}`.trim()
                      : user.username || 'No Name'
                  }
                </Typography>

                {/* Username Display */}
                <Typography variant="body2" className="user-card-username" sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  mb: 1,
                  fontSize: '0.9rem'
                }}>
                  @{TrimText(user.username, 20)}
                </Typography>

                <Chip
                  label={user.status === 1 ? 'Active' : user.status === 3 ? 'Self Excluded' : 'Inactive'}
                  size="small"
                  className="user-card-status-chip"
                  sx={{
                    backgroundColor: user.status === 1
                      ? 'rgba(76, 175, 80, 0.2)'
                      : user.status === 3
                        ? 'rgba(255, 152, 0, 0.2)'
                        : 'rgba(244, 67, 54, 0.2)',
                    color: user.status === 1
                      ? '#4caf50'
                      : user.status === 3
                        ? '#ff9800'
                        : '#f44336',
                    fontWeight: 600,
                    border: '1px solid',
                    borderColor: user.status === 1
                      ? 'rgba(76, 175, 80, 0.3)'
                      : user.status === 3
                        ? 'rgba(255, 152, 0, 0.3)'
                        : 'rgba(244, 67, 54, 0.3)'
                  }}
                />
              </Box>
            </Box>

            <Divider className="user-card-divider" />

            {/* User Details - Improved Layout */}
            <Box className="user-card-details-compact">
              <Grid container spacing={1}>
                {/* Email - Full width for better readability */}
                <Grid item xs={12}>
                  <Box className="user-card-detail-item-compact">
                    <Typography variant="caption" className="user-card-detail-label-compact">
                      Email
                    </Typography>
                    <Typography variant="body2" className="user-card-detail-value-compact">
                      {user.email || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>

                {/* Country and Phone in one row */}
                <Grid item xs={6}>
                  <Box className="user-card-detail-item-compact">
                    <Typography variant="caption" className="user-card-detail-label-compact">
                      Country
                    </Typography>
                    <Typography variant="body2" className="user-card-detail-value-compact">
                      {user.country || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box className="user-card-detail-item-compact">
                    <Typography variant="caption" className="user-card-detail-label-compact">
                      Phone
                    </Typography>
                    <Typography variant="body2" className="user-card-detail-value-compact">
                      {user.phoneNumber || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>

                {/* Age and Created At in one row */}
                <Grid item xs={6}>
                  <Box className="user-card-detail-item-compact">
                    <Typography variant="caption" className="user-card-detail-label-compact">
                      Age
                    </Typography>
                    <Typography variant="body2" className="user-card-detail-value-compact">
                      {calculateAge(user.dob)} years
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box className="user-card-detail-item-compact">
                    <Typography variant="caption" className="user-card-detail-label-compact">
                      Created At
                    </Typography>
                    <Typography variant="body2" className="user-card-detail-value-compact">
                      {fDate(user.createdAt)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider className="user-card-divider" />

            <Box className="user-card-actions">
              <Tooltip title="View">
                <IconButton
                  size="small"
                  className="user-action-button-view"
                  onClick={() => {
                    setModalShow(true);
                    setViewId(user._id);
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  className="user-action-button-edit"
                  onClick={() => navigate(`/user-management/edit/${user._id}?page=${currentPage}&view=${viewMode}`)}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  className="user-action-button-delete"
                  onClick={() => deleteData(user._id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box className="user-card-status-container">
              <Typography variant="caption" className="user-card-status-text">
                {user.status === 1 ? '🟢 Active' : user.status === 3 ? '🟡 Self Excluded' : '🔴 Inactive'}
              </Typography>
              {user.status !== 3 && (
                <Switch
                  checked={user.status === 1}
                  onChange={() => {
                    setShow(true);
                    setCurrentStatus(user.status);
                    setStatusId(user._id);
                    setWarning(
                      `Are you sure you want to ${user.status === 1 ? "deactivate" : "activate"} this user?`
                    );
                  }}
                  className="user-card-status-switch"
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Zoom>
  );

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "100px"
    },
    {
      name: "User Name",
      sortable: true,
      cell: (row) => {
        const fullName = (row.firstname && row.lastname)
          ? `${row.firstname} ${row.lastname}`
          : (row.firstname || row.lastname)
            ? `${row.firstname || ''} ${row.lastname || ''}`.trim()
            : row.username || 'No Name';

        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
              {TrimText(fullName, 20)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              @{TrimText(row.username, 15)}
            </Typography>
          </Box>
        );
      },
    },
    {
      name: "Age",
      sortable: true,
      selector: (row) => calculateAge(row.dob),
    },
    {
      name: "Country",
      sortable: true,
      selector: (row) => row.country || '-',
    },
    {
      name: "Email",
      sortable: true,
      selector: (row) => row.email || '-',
    },
    {
      name: "Phone",
      sortable: true,
      selector: (row) => row.phoneNumber || '-',
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => capitalize(fDate(row.createdAt)),
    },
    {
      name: "Status",
      cell: (row) =>
        row.status === 3 ? (
          <Chip
            label="Self Excluded"
            size="small"
            sx={{
              backgroundColor: 'rgba(255, 152, 0, 0.2)',
              color: '#ff9800',
              fontWeight: 600,
              border: '1px solid',
              borderColor: 'rgba(255, 152, 0, 0.3)'
            }}
          />
        ) : (
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={row.status === 1}
                  onClick={() => {
                    setShow(true);
                    setCurrentStatus(row.status);
                    setStatusId(row._id);
                    setWarning(
                      `Are you sure you want to ${row.status === 1 ? "deactivate" : "activate"
                      } this user?`
                    );
                  }}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
            />
          </FormGroup>
        ),
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                setModalShow(true);
                setViewId(row._id);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit" placement="left">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/user-management/edit/${row?._id}?page=${currentPage}&view=${viewMode}`);
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" placement="top">
            <IconButton color="primary" onClick={() => deleteData(row._id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Box className="user-management-container">
      <Helmet>
        <title>User Management</title>
      </Helmet>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          {/* Header Section */}
          <Paper
            elevation={0}
            className="user-header-paper"
          >
            <Box className="user-header-content">
              <Box className="user-header-left">
                <Avatar className="user-header-avatar">
                  <PersonIcon className="user-header-icon" />
                </Avatar>
                <Box>
                  <Typography variant="h4" className="user-header-title">
                    User Management
                  </Typography>
                  <Typography variant="body1" className="user-header-subtitle">
                    Manage and monitor all users in your system
                  </Typography>
                </Box>
              </Box>

              <Button
                size="large"
                variant="contained"
                className="user-add-button"
                onClick={() => navigate("/user-management/add")}
              >
                + Add User
              </Button>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={3} className="user-stats-grid">
              <Grid item xs={12} sm={6} md={3}>
                <Paper className="user-stat-card">
                  <Typography variant="h4" className="user-stat-number">
                    {allUsers?.length || 0}
                  </Typography>
                  <Typography variant="body2" className="user-stat-label">
                    Total Users
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper className="user-stat-card-active">
                  <Typography variant="h4" className="user-stat-number-active">
                    {totalActiveUsers}
                  </Typography>
                  <Typography variant="body2" className="user-stat-label">
                    Active Users
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper className="user-stat-card-inactive">
                  <Typography variant="h4" className="user-stat-number-inactive">
                    {totalInactiveUsers}
                  </Typography>
                  <Typography variant="body2" className="user-stat-label">
                    Inactive Users
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper className="user-stat-card-excluded">
                  <Typography variant="h4" className="user-stat-number-excluded">
                    {totalSelfExcludedUsers}
                  </Typography>
                  <Typography variant="body2" className="user-stat-label">
                    Self Excluded
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {/* Controls Section */}
          <Paper
            elevation={0}
            className="user-controls-paper"
          >
            <Box className="user-controls-content">
              <Box className="user-controls-left">
                <TableFilter
                  data={country.map(c => ({ value: c?.name, label: c?.name }))}
                  value={selectedCountry}
                  setValue={handleCountryChange}
                  filterName="Country"
                />

                <TableFilter
                  data={STATUS_OPTIONS_LIST1}
                  value={status}
                  setValue={handleStatusChange}
                  filterName="Status"
                />

                <Input
                  value={searchItem}
                  placeholder="Search users..."
                  onChange={handleSearchChange}
                  className="user-search-input"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear-search"
                        onClick={() => {
                          const cleared = "";
                          setSearchItem(cleared);
                          debouncedSearch(cleared);
                        }}
                        className="user-clear-search-button"
                      >
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Box>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode) {
                    setViewMode(newMode);
                    // Update URL parameter to maintain view mode
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.set('view', newMode);
                    setSearchParams(newSearchParams);
                  }
                }}
                className="user-view-toggle"
              >
                <ToggleButton value="grid">
                  <ViewModuleIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Paper>

          {/* Content Section */}
          <Paper
            elevation={0}
            className="user-content-paper"
          >
            {viewMode === 'grid' ? (
              /* Grid View */
              <Box className="user-grid-container">
                {isLoading || !loaderDelay ? (
                  <Box className="user-loading-container">
                    <Loader />
                  </Box>
                ) : !data || data.length === 0 ? (
                  <Box className="user-empty-container">
                    <Typography variant="h6" className="user-empty-title">
                      No users found
                    </Typography>
                    <Typography variant="body2" className="user-empty-subtitle">
                      Try adjusting your search criteria or create a new user
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3} className="user-grid">
                      {data.map((user, index) => renderUserCard(user, index))}
                    </Grid>

                    {/* Pagination for Grid View */}
                    <Box className="user-pagination-container">
                      <Box className="user-pagination-info">
                        <Typography variant="body2" className="user-pagination-text">
                          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalPages)} of {totalPages} users
                        </Typography>
                      </Box>
                      <Box className="user-pagination-controls">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ color: 'white', whiteSpace: 'nowrap' }}>
                            Items per page:
                          </Typography>
                          <FormControl size="small" className="user-page-size-select">
                            <Select
                              value={pageSize}
                              onChange={(e) => setPageSize(e.target.value)}
                              displayEmpty
                              sx={{
                                color: 'white',
                                minWidth: 80,
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(255, 255, 255, 0.3)',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(255, 255, 255, 0.5)',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(255, 255, 255, 0.7)',
                                },
                                '& .MuiSvgIcon-root': {
                                  color: 'white',
                                },
                              }}
                            >
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={20}>20</MenuItem>
                              <MenuItem value={50}>50</MenuItem>
                              <MenuItem value={100}>100</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={currentPage === 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" sx={{ color: 'white', mx: 2 }}>
                          Page {currentPage} of {Math.ceil(totalPages / pageSize)}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={currentPage >= Math.ceil(totalPages / pageSize)}
                          onClick={() => handlePageChange(currentPage + 1)}
                          sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.3)' }}
                        >
                          Next
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            ) : (
              /* List View */
              <Box>
                <DataTable
                  customStyles={{
                    headCells: {
                      style: {
                        fontSize: "15px",
                        fontWeight: "bold",
                        backgroundColor: 'rgba(0, 161, 221, 0.1)',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#333'
                      },
                    },
                    rows: {
                      style: {
                        backgroundColor: 'transparent',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        }
                      }
                    }
                  }}
                  columns={columns}
                  data={data}
                  pagination
                  paginationServer
                  paginationTotalRows={totalPages}
                  paginationCurrentPage={currentPage}
                  paginationDefaultPage={currentPage}
                  onChangePage={handlePageChange}
                  paginationPerPage={pageSize}
                  paginationRowsPerPageOptions={[10, 20, 50]}
                  onChangeRowsPerPage={(currentRowsPerPage) =>
                    setPageSize(currentRowsPerPage)
                  }
                  progressPending={isLoading || !loaderDelay}
                  progressComponent={<Loader />}
                  className="user-datatable"
                />
              </Box>
            )}
          </Paper>
        </Stack>
      </Fade>

      {/* Status Change Dialog */}
      <Dialog open={show} onClose={() => setShow(false)} className="user-status-dialog">
        <DialogTitle className="user-status-dialog-title">{warning}</DialogTitle>
        <DialogActions className="user-status-dialog-actions">
          <Button
            variant="outlined"
            onClick={() => {
              toggleChecked(statusId, currentStatus);
              setShow(false);
            }}
            color="primary"
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShow(false)}
            color="primary"
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
      {modalShow ? (
        <ViewUserManagement
          show={modalShow}
          onHide={() => setModalShow(false)}
          id={viewId}
        />
      ) : (
        ""
      )}
    </Box>
  );
}
